import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ProfessionalCategory {
  id: string;
  icon: string;
  name_it: string;
  name_en: string | null;
  name_es: string | null;
  keywords_it: string[] | null;
  keywords_en: string[] | null;
  keywords_es: string[] | null;
  display_order: number | null;
  is_active: boolean | null;
}

// Normalized category structure for compatibility with legacy code
export interface NormalizedCategory {
  id: string;
  icon: string;
  it: { name: string; keywords: string[] };
  en: { name: string; keywords: string[] };
  es: { name: string; keywords: string[] };
}

// Transform DB category to normalized format
export function normalizeCategory(cat: ProfessionalCategory): NormalizedCategory {
  return {
    id: cat.id,
    icon: cat.icon,
    it: { 
      name: cat.name_it, 
      keywords: cat.keywords_it || [] 
    },
    en: { 
      name: cat.name_en || cat.name_it, 
      keywords: cat.keywords_en || cat.keywords_it || [] 
    },
    es: { 
      name: cat.name_es || cat.name_it, 
      keywords: cat.keywords_es || cat.keywords_it || [] 
    }
  };
}

// Versioned query key to avoid cache poisoning from old prefetch
export const CATEGORIES_QUERY_KEY = 'professional-categories-v2';

// Main hook to fetch categories from database
export function useProfessionalCategories(onlyActive = true) {
  return useQuery({
    queryKey: [CATEGORIES_QUERY_KEY, onlyActive],
    queryFn: async (): Promise<NormalizedCategory[]> => {
      let query = supabase
        .from('professional_categories')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (onlyActive) {
        query = query.eq('is_active', true);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }
      
      return (data || []).map(normalizeCategory);
    },
    staleTime: 1000 * 60 * 10, // 10 minutes cache
    gcTime: 1000 * 60 * 30, // 30 minutes in memory
    retry: 2,
  });
}

// Helper: search categories by query (for use with fetched data)
export function searchCategoriesFromData(
  categories: NormalizedCategory[],
  query: string, 
  language: 'it' | 'en' | 'es' = 'it', 
  excludeIds: string[] = []
): NormalizedCategory[] {
  const normalizedQuery = query.toLowerCase().trim();
  
  let results = categories;
  
  // Exclude already selected
  if (excludeIds.length > 0) {
    results = results.filter(cat => !excludeIds.includes(cat.id));
  }
  
  if (!normalizedQuery) return results;
  
  return results.filter(category => {
    const categoryData = category[language];
    const nameMatch = categoryData.name.toLowerCase().includes(normalizedQuery);
    const keywordsMatch = categoryData.keywords.some(keyword => 
      keyword.toLowerCase().includes(normalizedQuery)
    );
    return nameMatch || keywordsMatch;
  });
}

// Helper: get category by ID from data
export function getCategoryByIdFromData(
  categories: NormalizedCategory[],
  id: string
): NormalizedCategory | undefined {
  return categories.find(cat => cat.id === id);
}
